import {
  PAYMENT_STATE,
  referAFriend as referAFriendInitState
} from '@apps/redux/features/PayAndReviewSlice'

// A plan's catalog promo ("auto promo") lives in the same multi-promo arrays as
// manually entered codes, so it renders in the order summary and is sent with
// every findAndValidatePromotions payload alongside any manual promos.
//
// Lifted verbatim from the pre-MFE PlansGrid.choosePlan / IntroPage blocks, so
// the MFE selection paths behave exactly as the plan card and deep link used to:
//   - arrays empty     -> seed with this plan's promo
//   - arrays populated -> keep only the entry belonging to this plan's promo,
//     dropping manually entered codes, so changing plan swaps the auto promo
//     instead of stacking the previous plan's on top
//
// `resetRaf` mirrors PlansGrid, which also cleared referral state when pruning;
// IntroPage's copy did not, so the pre-select path leaves it alone.
//
// Returns a payload for updatePaymentStates, or null when nothing should change.
export const buildAutoPromoState = ({
  promo,
  multiPromotionCodeInfo = [],
  multiPromoCodes = [],
  autoPromoText,
  enableMultiPromo,
  resetRaf = false
} = {}) => {
  if (!enableMultiPromo) return null

  const hadEntries = multiPromotionCodeInfo.length > 0 || multiPromoCodes.length > 0
  // Without autoPromoText there is no valid code to pair with the promo record,
  // so the auto promo cannot be applied: `[promo]` + `[undefined]` would send a
  // junk code to findAndValidatePromotions and count as a desynced pair.
  const canApply = Boolean(promo && autoPromoText)

  // Selecting a plan resets the promo state to exactly that plan's auto promo:
  // manually entered codes are dropped, and a previous plan's auto promo is
  // replaced rather than stacked.
  //
  // PlansGrid.choosePlan pruned by filtering `info` on the new promo's id, which
  // could only match when two plans shared a promo id. On a real plan change it
  // emptied `info` while leaving `autoPromoText` in `codes`, so the arrays fell
  // out of step: findAndValidatePromotions was still sent the auto promo code
  // while the order summary had no promo record to render (MASC-17223).
  const next = {
    [PAYMENT_STATE.PROMO.MULTI_PROMOTION_CODE_INFO]: canApply ? [promo] : [],
    [PAYMENT_STATE.PROMO.MULTI_PROMO_CODES]: canApply ? [autoPromoText] : []
  }

  // When the auto promo cannot be applied both arrays are cleared — manually
  // entered codes included, not just the previous plan's auto promo. That is
  // deliberate: selecting a plan resets promo state. Nothing to do only when
  // there was nothing there to begin with.
  if (!canApply && !hadEntries) return null

  return {
    ...next,
    ...(resetRaf && hadEntries
      ? { ...referAFriendInitState, [PAYMENT_STATE.RAF.FRIEND_REFERRAL_CODE_IS_VALID]: false }
      : {})
  }
}

// Drops the manually entered promo codes while leaving the plan's auto promo in
// place. Before the MFE work nothing cleared these arrays between pages, so
// emptying them wholesale takes the auto promo with it.
export const keepOnlyAutoPromo = ({
  promo,
  multiPromotionCodeInfo = [],
  autoPromoText
} = {}) => {
  // Without autoPromoText there is no way to tell the auto promo code from a
  // manual one, and filtering `codes` by `=== undefined` would empty it while
  // `info` kept the promo — the same out-of-step state this module exists to
  // prevent. Leave both arrays untouched instead; callers spread the result, so
  // null is a no-op. Stale manual codes are recoverable, a desynced pair is not.
  if (!autoPromoText) return null

  if (!promo) {
    return {
      [PAYMENT_STATE.PROMO.MULTI_PROMOTION_CODE_INFO]: [],
      [PAYMENT_STATE.PROMO.MULTI_PROMO_CODES]: []
    }
  }

  // Prefer the record already in state — after findAndValidatePromotions it is
  // the API's normalised version rather than the raw catalog promo — but fall
  // back to the plan's own promo so both arrays always end up with one entry.
  const validated = multiPromotionCodeInfo.filter((code) => code?.id === promo?.id)

  return {
    [PAYMENT_STATE.PROMO.MULTI_PROMOTION_CODE_INFO]: validated.length > 0 ? validated : [promo],
    [PAYMENT_STATE.PROMO.MULTI_PROMO_CODES]: [autoPromoText]
  }
}
